/**
 * The better-auth browser client (design §2.4: web = session cookie).
 *
 * `@crm/auth` is deliberately NOT imported: it constructs the SERVER instance
 * and pulls `@crm/db`, Drizzle and the whole Postgres stack. The browser needs
 * the client half only, and importing the server package would put the auth
 * secret's type — and a great deal of dead code — into the SPA bundle.
 *
 * ## What is real and what is not, today
 *
 * `signIn.email` posts to `POST /api/auth/sign-in/email`. `packages/auth`'s
 * `createAuth` only enables `emailAndPassword` when its caller passes it, and
 * `apps/api` does not yet — so against the current API this returns an error
 * rather than a session. That is the seam, not a bug in this file: the login
 * screen surfaces the server's answer verbatim instead of pretending to sign in.
 */

import { organizationClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { AUTH_BASE_URL } from '../env.ts';

export const authClient = createAuthClient({
  baseURL: AUTH_BASE_URL,
  // Org == tenant. The active organization on the session is what every query
  // key is scoped by, so the client needs the plugin's `organization.*` surface
  // to read and switch it.
  plugins: [organizationClient()],
});

export type SessionSnapshot = {
  readonly userId: string;
  readonly email: string;
  readonly name: string | null;
  readonly activeOrgId: string | null;
};

type SessionUser = { id?: unknown; email?: unknown; name?: unknown };
type SessionRecord = { activeOrganizationId?: unknown };

/**
 * Reads the current session, normalised to the four fields the app uses.
 *
 * Returns `null` for "not signed in" and THROWS for "could not ask". Collapsing
 * the two would send a user with a flaky connection to the login screen and
 * silently discard their session.
 */
export async function fetchSession(): Promise<SessionSnapshot | null> {
  const { data, error } = await authClient.getSession();
  if (error !== null && error !== undefined) {
    if (error.status === 401) {
      return null;
    }
    throw new Error(error.message ?? 'Could not read the session');
  }
  if (data === null || data === undefined) {
    return null;
  }

  const user = (data as { user?: SessionUser }).user ?? {};
  const session = (data as { session?: SessionRecord }).session ?? {};
  if (typeof user.id !== 'string') {
    return null;
  }
  return {
    userId: user.id,
    email: typeof user.email === 'string' ? user.email : '',
    name: typeof user.name === 'string' && user.name !== '' ? user.name : null,
    activeOrgId:
      typeof session.activeOrganizationId === 'string' ? session.activeOrganizationId : null,
  };
}

export async function signInWithPassword(input: {
  email: string;
  password: string;
}): Promise<void> {
  const { error } = await authClient.signIn.email({
    email: input.email,
    password: input.password,
  });
  if (error !== null && error !== undefined) {
    // A 429 arrives with better-auth's own terse body; "Sign-in failed" over a
    // rate limit reads as "wrong password" and makes people try harder, which
    // is exactly the wrong response. Say what actually happened and for how
    // long — the server sends `Retry-After` in seconds.
    if (error.status === 429) {
      const retry = typeof error.message === 'string' ? error.message.match(/\d+/)?.[0] : null;
      throw new Error(
        retry === null || retry === undefined
          ? 'Too many sign-in attempts. Wait a minute and try again.'
          : `Too many sign-in attempts. Try again in ${retry} seconds.`,
      );
    }
    throw new Error(error.message ?? 'Sign-in failed');
  }
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}
